//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import React, { Component } from 'react';
import { connect } from 'react-redux';
import Redux, { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'unison';

import { PrimaryButton } from '@components/atoms/button/Button';
import {
  SmallTooltip,
  SmallTooltipForIcon,
} from '@components/molecules/tooltip/Tooltip';
import { ICuralateStore } from '@models/AppState';
import * as Permissions from '@services/permissions';
import { streamCreationActions } from '@streams/actions/streamCreationActions';
import StreamExcludedSourceSelector from '@streams/components/StreamsExcludedSourceSelector';
import StreamsSourceSelector from '@streams/components/StreamsSourceSelector';
import { getShouldNotAllowUgcStreams } from '@streams/selectors/getShouldNotAllowUgcStreams';
import theme from '@theme/theme';
import gaApi from 'app/initialization/ga/ga-api';

function mapStateToProps(state: ICuralateStore) {
  const { streamCreation } = state.streams;

  return {
    currentSource: streamCreation.sourceSelector,
    errors: streamCreation.errors,
    existingStream: streamCreation.existingStream,
    hasOutstandingSaveRequest: streamCreation.hasOutstandingSaveRequest,
    isShareV2Vendor: state.session.client.isShareV2Vendor,
    sourcesAdded: streamCreation.sourcesAdded,
    streamName: streamCreation.streamName,
    streams: state.streams.streamsDrawer.streams,
    isGalleryExclusionsEnabled:
      state.session.features.galleries_streams_exclusions,
    shouldNotAllowUgcStreams: getShouldNotAllowUgcStreams(state),
  };
}

function mapDispatchToProps(dispatch: Redux.Dispatch) {
  return {
    actions: {
      streams: bindActionCreators(streamCreationActions, dispatch),
    },
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
export class StreamsCreationModal extends Component<Props> {
  public componentDidMount() {
    this.props.actions.streams.loadExistingSourcesState();
  }

  public validateInputs(): boolean {
    const {
      actions,
      currentSource,
      existingStream,
      sourcesAdded,
      streamName,
      streams,
    } = this.props;

    let errorCount = 0;

    if (!streamName) {
      actions.streams.setStreamNameError('You must name your stream.');
      errorCount++;
    } else {
      // exclude the existing stream's current name from being matched as a duplicate
      const isSameAsCurrent =
        existingStream && existingStream.name === streamName;
      const isDuplicateStreamName =
        !isSameAsCurrent &&
        streams.some((stream) => stream.name === streamName);

      if (isDuplicateStreamName) {
        actions.streams.setStreamNameError(
          'Stream name already in use. Choose a different name.',
        );
        errorCount++;
      }
    }

    // if 1+ source has already been added, skip further checking for errors
    if (!sourcesAdded.length) {
      if (!currentSource.sourceType) {
        actions.streams.setSourceTypeError('You must enter a source type.');
        errorCount++;
      }

      // make sure the user both entered text AND clicked "Add"
      if (!currentSource.sourceValue) {
        actions.streams.setSourceError('You must enter a source.');
        errorCount++;
      } else {
        actions.streams.setSourceError(
          'You must enter a source. Make sure you clicked "Add" after entering the source.',
        );
        errorCount++;
      }
    }

    return errorCount === 0;
  }

  public createOrUpdateStream = async () => {
    const { actions, errors, existingStream } = this.props;

    const isFormValid = this.validateInputs();
    if (!isFormValid) {
      return;
    }

    await this.props.actions.streams.createOrUpdateStream();

    if (!errors.createStreamError) {
      if (existingStream) {
        gaApi.recordEvent(
          gaApi.CATEGORY_STREAMS,
          gaApi.ACTION_STREAMS_EDIT_STREAM,
        );
      } else {
        gaApi.recordEvent(
          gaApi.CATEGORY_STREAMS,
          gaApi.ACTION_STREAMS_CREATE_STREAM,
        );
      }

      await actions.streams.closeNewStreamModal();
    }
  };

  public handleNameChange = (e: React.FormEvent<HTMLInputElement>) => {
    this.props.actions.streams.changeStreamName(e.currentTarget.value);
  };

  public renderSaveButton() {
    const {
      existingStream,
      hasOutstandingSaveRequest,
      sourcesAdded,
      streamName,
    } = this.props;

    const createButtonText = existingStream
      ? 'Save Changes and Close'
      : 'Create';

    const isCreateButtonDisabled = hasOutstandingSaveRequest;

    if (!Permissions.hasPermissionForTask('library:manage_collections')) {
      return (
        <SmallTooltipForIcon
          target={
            <DisabledNotHitTestableButton isDisabled={true}>
              {createButtonText}
            </DisabledNotHitTestableButton>
          }
          content={
            <TooltipContentWrapper>
              You don't have permission to take this action. Contact a group
              admin if this is a mistake.
            </TooltipContentWrapper>
          }
        />
      );
    } else {
      return (
        <SmallTooltip
          isDisabled={!isCreateButtonDisabled}
          target={
            <PrimaryButton
              isDisabled={isCreateButtonDisabled}
              onClick={this.createOrUpdateStream}
            >
              {createButtonText}
            </PrimaryButton>
          }
          content={
            <TooltipContentWrapper>
              {!streamName && <div>Stream name is required.</div>}
              {sourcesAdded.length < 1 && (
                <div>At least 1 source is required.</div>
              )}
            </TooltipContentWrapper>
          }
        />
      );
    }
  }

  public render() {
    const {
      actions,
      errors,
      existingStream,
      isGalleryExclusionsEnabled,
      isShareV2Vendor,
      streamName,
      shouldNotAllowUgcStreams,
    } = this.props;

    const isNonUgcShareVendor = isShareV2Vendor && shouldNotAllowUgcStreams;

    const createStreamCtaText = existingStream
      ? existingStream.name
      : 'Create a New Media Stream';

    return (
      <Modal isOpen={true}>
        <ModalHeader onCancel={actions.streams.closeNewStreamModal}>
          <div>
            {isNonUgcShareVendor ? 'Edit Sources' : createStreamCtaText}
          </div>
        </ModalHeader>
        <ModalBody className='cu-d-block cu-wide-measure'>
          <ModalContainer>
            {!isNonUgcShareVendor && !existingStream && (
              <StreamNameWrapper className='cu-mb-4'>
                <div className='cu-text-ui-copy-2 cu-mb-2'>Stream Name</div>
                <FullWidthInputWrapper>
                  <input
                    className='cu-form-control'
                    defaultValue={streamName}
                    onChange={this.handleNameChange}
                    placeholder='Enter a Stream Name'
                    type='text'
                    disabled={shouldNotAllowUgcStreams}
                  />
                </FullWidthInputWrapper>
                {errors.streamNameError && (
                  <Error>{errors.streamNameError}</Error>
                )}
              </StreamNameWrapper>
            )}
            <StreamsSourceSelector />
            {isGalleryExclusionsEnabled && <StreamExcludedSourceSelector />}
            {errors.createStreamError && (
              <Error>{errors.createStreamError}</Error>
            )}
          </ModalContainer>
        </ModalBody>
        <ModalFooter>
          <RightAlign>{this.renderSaveButton()}</RightAlign>
        </ModalFooter>
      </Modal>
    );
  }
}

const ModalContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const DisabledNotHitTestableButton = styled(PrimaryButton)`
  pointer-events: none;
`;

const TooltipContentWrapper = styled.div`
  text-align: center;
`;

const StreamNameWrapper = styled.div`
  display: block;
`;

const FullWidthInputWrapper = styled.div`
  input {
    width: 100%;
  }
`;

const Error = styled.div`
  color: ${theme.colors.aliases.error};
  margin-bottom: ${theme.spacings[1]};
`;

const RightAlign = styled.div`
  margin-left: auto;
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StreamsCreationModal);
